import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import logoSvg from '../../assets/logo.png';
import { RiUserLine, RiLockLine, RiEyeLine, RiEyeOffLine } from 'react-icons/ri';

export default function PortalLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (user) {
    return <Navigate to={user.role === 'admin' ? '/portal/admin' : '/portal/employee'} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(form.username, form.password);
      navigate(userData?.role === 'admin' ? '/portal/admin' : '/portal/employee', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid username or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] bg-mesh-soft flex items-center justify-center px-4 relative">

      {/* Subtle dot grid */}
      <div className="absolute inset-0 bg-dots opacity-40 pointer-events-none" />

      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(9,132,227,0.15) 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(213,0,249,0.15) 0%, transparent 70%)' }} />

      <div className="relative w-full max-w-md">

        {/* Card */}
        <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_20px_60px_rgba(15,23,42,0.1)] p-8">

          {/* Header */}
          <div className="flex flex-col items-center mb-8">
            <img src={logoSvg} alt="Nexcess Infotech" className="w-14 h-14 object-contain mb-3" />
            <h1 className="text-2xl font-bold text-[#1F2937] tracking-tight">Employee Portal</h1>
            <p className="text-[#6B7280] text-sm mt-1 font-medium tracking-wide uppercase text-xs">
              NEXCESS INFOTECH
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-semibold text-[#1F2937] mb-1.5">
                Username
              </label>
              <div className="relative">
                <RiUserLine className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={17} />
                <input
                  id="username"
                  type="text"
                  required
                  autoComplete="username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="your username"
                  className="input-field pl-10"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-[#1F2937] mb-1.5">
                Password
              </label>
              <div className="relative">
                <RiLockLine className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={17} />
                <input
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Enter your password"
                  className="input-field pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280] transition-colors cursor-pointer"
                  aria-label="Toggle password"
                >
                  {showPass ? <RiEyeOffLine size={17} /> : <RiEyeLine size={17} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full justify-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="text-center text-[#9CA3AF] text-xs mt-6 leading-relaxed">
            Use credentials provided by your administrator.
          </p>
        </div>

        {/* Back link */}
        <p className="text-center mt-5">
          <a href="/" className="text-[#6B7280] hover:text-[#0984e3] text-sm font-medium transition-colors cursor-pointer">
            ← Back to Website
          </a>
        </p>
      </div>
    </div>
  );
}
